import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup, FormControl, Validators } from "@angular/forms";
import { MatTabGroup } from "@angular/material";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, tap, map, switchMap, takeUntil } from "rxjs/operators";

import { EnquiryService } from "../shared/enquiry.service";
import { ValidationHelperService } from "../shared/validation-helper.service";

import { atLeastOne } from "../shared/at-least-one-validator";
import { DropdownOption } from "../shared/dropdown-option.model";
import { Enquiry } from "../shared/enquiry.model";
import { EnquiryOptions } from "../shared/enquiry-options.model";
import { IntroducerRateChart } from "../shared/introducer-rate-chart.model";
import { RepaymentCalculation } from "../shared/repayment-calculation.model";
import { RepaymentOption } from "../shared/repayment-option.model";

@Component({
    template: require('./enquiry-detail.component.html'),
    styles: [require('./enquiry-detail.component.css')]
})
export class EnquiryDetailComponent implements OnInit, OnDestroy {
    enquiryForm: FormGroup;
    opts: EnquiryOptions;
    errorMessage: string;
    loadingEnquiryOptions: boolean;
    loadingRepaymentOptions: boolean;
    loadingSubmission: boolean;
    selectedIrc: IntroducerRateChart;
    repaymentOptionFC: FormControl;

    captchaResponse: string;
    token: string;
    validationMessage: string;

    private currencyInputRegex = /^[1-9]\d*(\.\d+)?$/; // Temp (until we can use number input instead of text) currency input (no leading zeroes). Use this - https://regexr.com/
    private ngUnsubscribe: Subject<void> = new Subject(); //see https://stackoverflow.com/a/41177163

    // using BehaviorSubject since we need to be able to push the current repaymentOpts into the email/download quote dialogs when they are opened.
    // see section on BehaviorSubject - http://blog.angular-university.io/how-to-build-angular2-apps-using-rxjs-observable-data-services-pitfalls-to-avoid/
    // UPDATE - this code file was initially copy pasted from FAWFinessePortal, this may not be necessary since the use case here is simplier
    private _repaymentOptions$: BehaviorSubject<RepaymentOption[]> = new BehaviorSubject([]);
    public readonly repaymentOptions$: Observable<RepaymentOption[]> = this._repaymentOptions$.asObservable();

    constructor(
        private enquiryService: EnquiryService,
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private validationHelperService: ValidationHelperService) {

        this.repaymentOptionFC = new FormControl();
        this.createForm();
    }

    ngOnInit() {
        this.loadingEnquiryOptions = true;

        this.activatedRoute.queryParamMap
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(params => {
                this.token = params.get("token");
            });

        // get the introducerRateCharts and automatically select the first one
        this.enquiryService.getOptions(this.token)
            .then(data => {
                this.loadingEnquiryOptions = false;
                this.opts = data;
                //Assume this.opts.introducerRateCharts[0] exists, since server will return an error response if no introducerRateCharts found.
                this.financeFG.get("introducerRateChartID").setValue(this.opts.introducerRateCharts[0].id);
            })
            .catch(err => {
                this.loadingEnquiryOptions = false;
                this.errorMessage = err;
                this.enquiryForm.disable();
            });

        // update form validators and assetTypeGroup dropdown when the introducerRateChart dropdown changes
        this.financeFG.get("introducerRateChartID").valueChanges
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(val => {
                if (this.opts.introducerRateCharts) {
                    this.selectedIrc = this.opts.introducerRateCharts.find(i => i.id === val);

                    if (this.selectedIrc) {
                        const assetTypeGroupFC = this.financeFG.get("assetTypeGroupID") as FormControl;
                        if (!this.selectedIrc.assetTypeGroups.find(a => a.id === assetTypeGroupFC.value)) {
                            assetTypeGroupFC.setValue(this.selectedIrc.assetTypeGroups[0].id);
                        }

                        this.costOfGoodsFC.setValidators(
                            [
                                Validators.required,
                                Validators.pattern(this.currencyInputRegex),
                                Validators.min(this.selectedIrc.minCostOfGoods),
                                Validators.max(this.selectedIrc.maxCostOfGoods)
                            ]);
                        this.costOfGoodsFC.updateValueAndValidity();

                        const isAdvanceFC = this.financeFG.get("isAdvance") as FormControl;
                        isAdvanceFC.setValue(this.selectedIrc.allowInAdvance);
                        (this.selectedIrc.allowInAdvance && this.selectedIrc.allowInArrears)
                            ? isAdvanceFC.enable()
                            : isAdvanceFC.disable();
                    }
                }
            });

        // automatically get repayment options when the finance area of the form is valid
        this.financeFG.valueChanges
            .pipe(
                takeUntil(this.ngUnsubscribe),
                debounceTime(500), //TODO: debounce also happens on initial load when opening an existing enquiry..
                distinctUntilChanged(), //TODO: doesnt do anything?
                switchMap(val => {
                    this.repaymentOptionFC.reset();

                    if (this.financeFG.valid) {
                        this.loadingRepaymentOptions = true;
                        return this.getRepaymentOptions();
                    } else {
                        return of<RepaymentOption[]>([]);
                    }
                }),
                tap(opts => {
                    this.loadingRepaymentOptions = false;
                    this._repaymentOptions$.next(opts);
                }),
                catchError(error => {
                    console.log(error);
                    return of<RepaymentOption[]>([]);
                })
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
    
    actionsDisabled(): boolean {
        return this.loadingEnquiryOptions
            || this.showRepaymentsSpinner()
            || this.showSubmissionSpinner()
            || this.errorMessage != null;
    }

    get costOfGoodsFC(): FormControl { return this.enquiryForm.get("finance.costOfGoods") as FormControl; }
    get financeFG(): FormGroup { return this.enquiryForm.get("finance") as FormGroup; }
    get phoneNumbersFG(): FormGroup { return this.enquiryForm.get("phoneNumbers") as FormGroup; }

    createForm(): void {
        this.enquiryForm = this.fb.group({
            // separate 'finance' formGroup so we can just validate this before getting repayment options
            finance: this.fb.group({
                introducerRateChartID: ['', Validators.required],
                assetTypeGroupID: ['', Validators.required],
                costOfGoods: ['', [Validators.required, Validators.pattern(this.currencyInputRegex)]],
                isAdvance: ['', Validators.required],
            }),

            phoneNumbers: this.fb.group({
                phone: ['', Validators.maxLength(20)],
                mobile: ['', Validators.maxLength(20)],
            }, { validator: atLeastOne(Validators.required) }),

            goodsDescription: ['', Validators.maxLength(1000)],
            contactName: ['', [Validators.required, Validators.maxLength(100)]],
            businessName: ['', [Validators.required, Validators.maxLength(100)]],
            abn: ['', [Validators.minLength(11), Validators.maxLength(11)]],
            emailAddress: ['', Validators.maxLength(100)],
            address: ['', Validators.maxLength(200)],
            notes: ['', Validators.maxLength(500)],
        });
    }

    getFilteredRepaymentOptions(paymentFrequencyID: number, opts: RepaymentOption[]): RepaymentOption[] {
        return opts.filter(r => r.paymentFrequencyID == paymentFrequencyID);
    }

    getRepaymentOptions(): Observable<RepaymentOption[]> {
        const calcParams: RepaymentCalculation = {
            introducerRateChartID: this.financeFG.get("introducerRateChartID").value,
            assetTypeGroupID: this.financeFG.get("assetTypeGroupID").value,
            costOfGoods: this.financeFG.get("costOfGoods").value,
            isAdvance: this.financeFG.get("isAdvance").value
        }

        return this.enquiryService.getRepaymentOptions(this.token, calcParams);
    }

    getResidualLabel(): string {
        return (this.selectedIrc && this.selectedIrc.labelResidualAsBalloon)
            ? "Balloon"
            : "Residual";
    }

    getSingularFrequencyDesc(paymentFreq: string): string {
        let singularDesc: string = null;
        switch (paymentFreq) {
            case 'Weekly':
                singularDesc = 'Week';
                break;
            case 'Monthly':
                singularDesc = 'Month';
                break;
            case 'Quarterly':
                singularDesc = 'Quarter';
                break;
            case 'Annual':
                singularDesc = 'Year';
                break;
            case 'Fortnightly':
                singularDesc = 'Fortnight';
                break;
            default:
                singularDesc = 'Period';
                break;
        }

        return singularDesc;
    }

    isRepaymentOptionSelected(opt: RepaymentOption): boolean {
        let isSelected = false;

        const selectedOpt = this.repaymentOptionFC.value as RepaymentOption;
        if (selectedOpt) {
            isSelected =
                opt.paymentFrequencyID === selectedOpt.paymentFrequencyID
                && opt.repayment === selectedOpt.repayment
                && opt.term === selectedOpt.term
                && opt.residualAmount === selectedOpt.residualAmount;
        }

        return isSelected;
    }

    isResidualLabelledAsBalloon(): boolean {
        return this.selectedIrc && this.selectedIrc.labelResidualAsBalloon;
    }

    // see https://angular.io/guide/reactive-forms#save-form-data
    prepareSubmitEnquiry(): Enquiry {
        const formModel = this.enquiryForm.value;
        const isAdvance = this.financeFG.get("isAdvance").value as boolean; // because the FormControl may be disabled
        const selectedOpt = this.repaymentOptionFC.value as RepaymentOption;

        // assigning a few extra fields here just so we don't need to look up the ID values again on the backend, before sending the email
        const assetTypeGroup = this.selectedIrc.assetTypeGroups.find(i => i.id === formModel.finance.assetTypeGroupID);

        const saveEnquiry: Enquiry = {
            id: null,
            enquiryNumber: null,

            // from formModel.finance
            introducerRateChartID: formModel.finance.introducerRateChartID as number,
            productName: this.selectedIrc.productName,
            programName: this.selectedIrc.programName,
            assetTypeGroupID: formModel.finance.assetTypeGroupID as number,
            assetTypeGroupName: assetTypeGroup.label,
            costOfGoods: formModel.finance.costOfGoods as number,
            isAdvance: isAdvance,

            // from formModel.phoneNumbers
            phoneNumber: formModel.phoneNumbers.phone as string,
            mobileNumber: formModel.phoneNumbers.mobile as string,

            // from formModel
            goodsDescription: formModel.goodsDescription as string,
            contactName: formModel.contactName as string,
            businessName: formModel.businessName as string,
            abn: formModel.abn as string,
            emailAddress: formModel.emailAddress as string,
            address: formModel.address as string,
            notes: formModel.notes as string,

            // from selected RepaymentOption
            paymentFrequencyID: selectedOpt ? selectedOpt.paymentFrequencyID : null,
            paymentFrequencyName: selectedOpt ? selectedOpt.paymentFrequencyName : null,
            repayment: selectedOpt ? selectedOpt.repayment : null,
            term: selectedOpt ? selectedOpt.term : null,
            residualAmount: selectedOpt ? selectedOpt.residualAmount : null,

            captcha: this.captchaResponse
        };

        return saveEnquiry;
    }

    resolved(captchaResponse: string) {
        //console.log(`Resolved captcha with response ${captchaResponse}:`);
        this.captchaResponse = captchaResponse;
    }

    showAssetTypeGroup(): boolean {
        return this.selectedIrc && this.selectedIrc.assetTypeGroups.length > 1;
    }

    showFinanceForm(): boolean {
        return this.opts && !this.errorMessage;
    }

    showFinanceInvalidMessage(): boolean {
        return this.financeFG.invalid;
    }

    showFinanceType(): boolean {
        return this.opts && this.opts.introducerRateCharts && this.opts.introducerRateCharts.length > 1;
    }

    showPhoneError(): boolean {
        return this.phoneNumbersFG.touched && this.phoneNumbersFG.hasError("atLeastOne");
    }

    showRepaymentsList(): boolean {
        return !this.loadingRepaymentOptions && this.financeFG.valid;
    }

    showRepaymentsSpinner(): boolean {
        return this.loadingRepaymentOptions && this.financeFG.valid;
    }

    showResidual(): boolean {
        return this.selectedIrc && this.selectedIrc.showResidualValue;
    }

    showSubmissionSpinner(): boolean {
        return this.loadingSubmission;
    }

    submit(): void {
        if (this.enquiryForm.invalid || !this.repaymentOptionFC.value || !this.captchaResponse) {
            this.validationHelperService.markAllTouched(this.enquiryForm);
            this.validationMessage = "Please fill in required values, select a repayment option, and verify you're not a robot.";
            return;
        }

        this.validationMessage = null;
        this.loadingSubmission = true;
        const enquiryToSubmit = this.prepareSubmitEnquiry();

        this.enquiryService.submit(this.token, enquiryToSubmit)
            .then(() => {
                this.loadingSubmission = false;
                this.router.navigate(["/submitted"]);
            })
            .catch(err => {
                this.validationMessage = "An error occurred during submission, details in console.";
                this.loadingSubmission = false;
            });
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./src/app/enquiry-detail/enquiry-detail.component.ts